import React, { memo, useCallback } from 'react';
import { Handle, Position } from 'reactflow';
import { Paper, Typography, Box, alpha } from '@mui/material';
import { Stop } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

const EndNode = memo(({ data, selected, id }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // End node uses theme error colors
  const endGradient = `linear-gradient(135deg, ${theme.palette.error.main} 0%, ${theme.palette.error.light} 100%)`;
  const textColor = theme.palette.error.contrastText;
  const borderColor = selected ? theme.palette.secondary.main : 'transparent';
  const hoverGradient = `linear-gradient(135deg, ${theme.palette.error.dark} 0%, ${theme.palette.error.main} 100%)`;

  const handleDoubleClick = useCallback((event) => {
    event.stopPropagation();
    const editEvent = new CustomEvent('editNode', { detail: { nodeId: id } });
    window.dispatchEvent(editEvent);
  }, [id]);

  const handleContextMenu = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    const contextEvent = new CustomEvent('nodeContextMenu', {
      detail: {
        nodeId: id,
        x: event.clientX,
        y: event.clientY
      }
    });
    window.dispatchEvent(contextEvent);
  }, [id]);

  return (
    <Paper
      elevation={selected ? 8 : 3}
      onDoubleClick={handleDoubleClick}
      onContextMenu={handleContextMenu}
      sx={{
        width: 60,
        height: 60,
        borderRadius: '50%',
        background: endGradient,
        color: textColor,
        border: `2px solid ${borderColor}`,
        cursor: 'move',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 0,
        transition: theme.transitions.create([
          'transform', 'box-shadow', 'background'
        ], {
          duration: theme.transitions.duration.shorter,
        }),
        '&:hover': {
          transform: 'translateY(-2px) scale(1.05)',
          boxShadow: theme.shadows[8],
          background: hoverGradient,
        },
        '&:focus-within': {
          outline: `2px solid ${theme.palette.secondary.main}`,
          outlineOffset: 2,
        },
        '&[aria-selected="true"]': {
          boxShadow: `0 0 0 3px ${alpha(theme.palette.secondary.main, 0.3)}`,
        },
        // Dashboard theme accent styling
        '&::before': {
          content: '""',
          position: 'absolute',
          top: -2,
          left: -2,
          right: -2,
          bottom: -2,
          background: `linear-gradient(45deg, ${alpha(theme.palette.primary.main, 0.3)}, ${alpha(theme.palette.secondary.main, 0.3)})`,
          borderRadius: 'inherit',
          zIndex: -1,
          opacity: selected ? 1 : 0,
          transition: theme.transitions.create('opacity'),
        },
      }}
      role="button"
      tabIndex={0}
      aria-label={`End node: ${data.label || 'End'}`}
      aria-selected={selected}
    >
      <Handle
        type="target"
        position={Position.Top}
        style={{
          background: theme.palette.text.primary,
          border: `2px solid ${theme.palette.background.paper}`,
          width: 12,
          height: 12,
          top: -6,
        }}
      />

      <Stop
        sx={{
          fontSize: 24,
          color: textColor,
          filter: isDark ? 'none' : 'brightness(1.1)',
          mb: 0.5,
        }}
      />
      <Typography
        variant="caption"
        fontWeight="600"
        sx={{
          color: textColor,
          fontSize: '0.65rem',
          lineHeight: 1,
          textAlign: 'center',
          textShadow: '0 1px 2px rgba(0,0,0,0.3)',
        }}
      >
        {data.label || 'End'}
      </Typography>

      {/* End indicator with dashboard colors */}
      <Box
        sx={{
          position: 'absolute',
          top: 4,
          right: 4,
          width: 8,
          height: 8,
          borderRadius: '50%',
          backgroundColor: theme.palette.secondary.main, // Gold accent
          border: `1px solid ${textColor}`,
          boxShadow: `0 0 6px ${theme.palette.secondary.main}`,
          animation: 'pulse 2s infinite',
          '@keyframes pulse': {
            '0%': {
              opacity: 1,
              transform: 'scale(1)',
            },
            '50%': {
              opacity: 0.7,
              transform: 'scale(1.2)',
            },
            '100%': {
              opacity: 1,
              transform: 'scale(1)',
            },
          },
        }}
      />
    </Paper>
  );
});

EndNode.displayName = 'EndNode';

export default EndNode;